
import { Component, Vue, Watch } from 'vue-property-decorator';
import { CompilationListItem, TrackListItem } from '@/types';
import { OrderDir, TagOrderColumn, TagService, TrackService, TrackServiceListParams } from '@/api';
import TrackTableList from '@/components/track/TrackTableList.vue';
import { TableField } from '@/ui-lib/table/Table.vue';
import UPagination from '@/ui-lib/pagination/Pagination.vue';
import { CompilationSelectModalParams } from '@/components/modals/CompilationSelectModal.vue';
import USelectField, {
  SelectMultipleValue,
  SelectOptions
} from '@/ui-lib/select-field/SelectField.vue';
import UTextField from '@/ui-lib/text-field/TextField.vue';

export type TracksSelectModalParams = {
  selected?: number[];
  onSelect?: () => void;
  excludePlaylistID?: number;
  excludeTagID?: number;
};

@Component({
  components: {
    USelectField,
    UPagination,
    TrackTableList,
    UTextField
  }
})
export default class extends Vue {
  private selected: number[] = [];
  private onSelect: () => void = () => {
    return;
  };
  private excludePlaylistID: number | null = null;
  private excludeTagID: number | null = null;

  private start = 0;
  private limit = 20;
  private search = '';
  private appliedSearch = '';
  private searchTimeout = 0;
  private loadRequestID = 0;
  private isLoading = false;
  private list: TrackListItem[] = [];
  private total = 0;
  private compilation: CompilationListItem | null = null;
  private tagsSelected: SelectMultipleValue = null;
  private tagsOptions: SelectOptions = [];
  private tagsDisabled = true;

  // Дебаунс живёт в методе, а не в поле-стрелке: инициализаторы полей
  // vue-class-component выполняются на объекте-коллекторе data, и захваченный
  // ими this не проксирует реактивные свойства — присвоения в него теряются.
  @Watch('search')
  private searchChanged() {
    clearTimeout(this.searchTimeout);

    this.searchTimeout = window.setTimeout(() => {
      if (this.search === this.appliedSearch) {
        return;
      }

      this.start = 0;
      this.load();
    }, 300);
  }

  private beforeClose() {
    clearTimeout(this.searchTimeout);

    // Модалка живёт в DOM постоянно: без инвалидации ответ уже отправленного
    // запроса всплыл бы тостом об ошибке поверх закрытого окна.
    this.loadRequestID++;
  }

  private close(): void {
    this.$modal.hide('tracks-select');
  }

  private beforeOpen({ params }: { params: TracksSelectModalParams }) {
    this.start = 0;
    this.isLoading = false;
    this.search = '';
    this.list = [];
    this.total = 0;
    this.compilation = null;

    this.tagsSelected = null;
    this.tagsOptions = [];
    this.tagsDisabled = true;

    this.selected = params?.selected || [];
    this.onSelect =
      params?.onSelect ||
      (() => {
        return;
      });
    this.excludePlaylistID = params?.excludePlaylistID || null;
    this.excludeTagID = params?.excludeTagID || null;

    Promise.all([this.load(), this.loadTags()]);
  }

  private async load() {
    this.isLoading = true;

    // Ответы приходят вразнобой при быстрой печати и переоткрытии модалки —
    // применяем только последний отправленный запрос.
    const requestID = ++this.loadRequestID;

    try {
      const params: TrackServiceListParams = {
        start: this.start,
        limit: this.limit
      };

      if (this.search) {
        params.search = this.search;
      }

      this.appliedSearch = this.search;

      if (this.compilation) {
        params.compilationsIds = [this.compilation.id];
      }

      if (this.excludePlaylistID) {
        params.excludePlaylistID = this.excludePlaylistID;
      }

      if (this.excludeTagID) {
        params.excludeTagID = this.excludeTagID;
      }

      if (this.tagsSelected) {
        const valInt = (val: string | number) => (typeof val === 'string' ? parseInt(val) : val);

        if (Array.isArray(this.tagsSelected)) {
          params.tagAndIDs = this.tagsSelected.map((f) => valInt(f.value));
        } else {
          params.tagAndIDs = [valInt(this.tagsSelected.value)];
        }
      }

      const { list, total } = await TrackService.list(params);

      if (requestID !== this.loadRequestID) {
        return;
      }

      this.list = list;
      this.total = total;
    } catch (e) {
      if (requestID !== this.loadRequestID) {
        return;
      }

      this.$notify({
        type: 'error',
        title: 'Ошибка',
        text: e?.message || 'Неизвестная ошибка'
      });
    }

    this.isLoading = false;
  }

  private get tableFields(): TableField[] {
    return [
      {
        name: 'select',
        label: '',
        width: '38px'
      },

      {
        name: 'play-pause',
        label: '',
        width: '40px'
      },

      {
        name: 'id',
        label: 'ID',
        width: '65px'
      },

      {
        name: 'title',
        label: 'Название'
      },

      {
        name: 'likes',
        label: 'Лайки',
        width: '60px',
        align: 'right'
      },

      {
        name: 'dislikes',
        label: 'Дизлайки',
        width: '60px',
        align: 'right'
      },

      {
        name: 'listeners_per_month',
        label: 'Слуш/мес',
        width: '60px',
        align: 'right'
      },

      {
        name: 'skips_per_month',
        label: 'Скип/мес',
        width: '60px',
        align: 'right'
      }
    ];
  }

  private changeStart(val: number) {
    clearTimeout(this.searchTimeout);

    // Неприменённый ввод в поиске делает выбранную страницу бессмысленной:
    // клик по пагинации применяет запрос с первой страницы.
    this.start = this.search === this.appliedSearch ? val : 0;
    this.load();
  }

  private selectCompilation() {
    const params: CompilationSelectModalParams = {
      onSelect: (val) => {
        this.start = 0;
        this.compilation = val;
        this.load();
      }
    };

    this.$modal.show('compilation-select', params);
  }

  private cleanCompilation() {
    this.start = 0;
    this.compilation = null;
    this.load();
  }

  private select() {
    this.onSelect();
    this.close();
  }

  private async loadTags() {
    this.tagsDisabled = true;

    try {
      const res = await TagService.list({
        start: 0,
        limit: 99999,
        order_column: TagOrderColumn.id,
        order_dir: OrderDir.desc
      });

      this.tagsOptions = res.list.map((tag) => ({
        label: tag.name,
        value: tag.id
      }));

      this.tagsDisabled = false;
    } catch (e) {
      this.$notify({ type: 'error', title: 'Ошибка', text: e?.message || 'Неизвестная ошибка' });
    }
  }

  // tagsDisabled принадлежит загрузке справочника тегов: блокировать селект на
  // время перезагрузки списка незачем — устаревшие ответы отсекает loadRequestID.
  @Watch('tagsSelected')
  private tagsSelectedChanged() {
    this.start = 0;
    this.load();
  }
}
